import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  cannotBePurchased,
  getSignedTypeDescription,
  getSignerLinks,
} from "./productAdapter";
import s from "./ProductHeader.module.css";

const TRUNCATE_LENGTH = 200;

TruncatedText.propTypes = {
  text: PropTypes.string.isRequired,
};

function TruncatedText({ text }) {
  const [expanded, setExpanded] = useState(false);
  const needsTruncation = text.length > TRUNCATE_LENGTH;
  const displayed =
    needsTruncation && !expanded
      ? `${text.slice(0, TRUNCATE_LENGTH)}\u2026`
      : text;

  return (
    <p className={s.descriptionText}>
      {displayed}
      {needsTruncation && (
        <>
          {" "}
          <button
            type="button"
            className={s.showMoreBtn}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "show less" : "show more"}
          </button>
        </>
      )}
    </p>
  );
}

ProductHeader.propTypes = {
  product: PropTypes.object.isRequired,
  resolvedDescription: PropTypes.string,
  selectedVariant: PropTypes.object,
};

export default function ProductHeader({
  product,
  resolvedDescription,
  selectedVariant,
}) {
  const signedType = selectedVariant?.signed_type ?? product.signed_type;
  const soldOut = cannotBePurchased(product);

  const resolvedForDesc = useMemo(
    () => ({
      ...product,
      signed_type: signedType,
    }),
    [product, signedType],
  );

  const description = useMemo(
    () => getSignedTypeDescription(resolvedForDesc),
    [resolvedForDesc],
  );

  const signerLinks = useMemo(() => getSignerLinks(product), [product]);

  const hasDetails =
    product.product_type || resolvedDescription || description?.text;

  if (!hasDetails && signerLinks.length === 0 && soldOut) return null;

  const productType = selectedVariant?.product_type || product.product_type;

  return (
    <div className={s.headerWrap}>
      {productType && <span className={s.productType}>{productType}</span>}

      {/* Signer links */}
      {signerLinks.length > 0 && (
        <p className={s.signerLinks}>
          Signed by:{" "}
          {signerLinks.map((link, i) => (
            <span key={link.name}>
              {link.to ? (
                <Link
                  className={s.signerLink}
                  to={link.to}
                  aria-label={`Go to ${link.name}'s shop`}
                >
                  {link.name}
                </Link>
              ) : (
                link.name
              )}
              {i < signerLinks.length - 1 && ", "}
            </span>
          ))}
        </p>
      )}

      {/* Product details shown directly */}
      {hasDetails && (
        <div className={s.productDetailsBody}>
          {resolvedDescription && <TruncatedText text={resolvedDescription} />}
          {description?.text && (
            <p className={s.signedTypeText}>
              {description.text}
              {description.note && (
                <>
                  <br />
                  <strong className={s.signedTypeNote}>
                    {description.note}
                  </strong>
                </>
              )}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
